import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { WorkerCompetencyWorkflowItem } from 'app/_models/workflow-items/worker-competency-workflow-item';
import { WorkflowItem } from 'app/_models/workflow-items/workflow-item.model';
import { ContractorService } from 'app/_services/contractor.service';
import { NotificationService } from 'app/_services/notification.service';
import { WorkflowAccessService } from 'app/_services/workflow-access.service';
import { WorkflowItemService } from 'app/_services/workflow-items.service';
import { canManageCompetencyForSseRoute, hasSseCompetencyRestrictions, isDelegateManagingSelf } from 'app/_utils/competency-permissions.utils';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { hasWorkflowBackParams, readWorkflowBackParams } from 'app/_utils/workflow-back-link.utils';
import { environment } from 'environments/environment';

@Component({
  selector: 'worker-certificate-approval',
  templateUrl: './worker-certificate-approval.component.html',
})
export class WorkerCertificateApprovalComponent implements OnInit {
  private workflowItemId: any;
  private routeRole: any;
  /** The inbox filters the user came in with, so every route home lands on the view they left. */
  private backLinkParams: any = {};
  public title = 'Worker Certificate Pending Approval';
  public workerCompetencyLink: any[] = [];
  public workerCompetencyLinkQueryParams: any = null;
  public canOpenWorkerCompetencyLink = false;
  public canOpenRequestedByDetails = false;
  public readonly outOfScopeMessage = 'This certificate can be viewed here, but it must be handled by the appropriate user role.';
  public readonly selfApprovalMessage = 'You cannot approve or reject your own certificate. Another Site Delegate or an Authorising Officer must review it.';

  public saveInProgress = false;
  public tabitems: any[] = [];
  public isViewOnly = false;
  public showApprovalForm = false;
  public approvalExpiryDate: Date | null = null;
  public approvalExpiryDateRequired = false;
  public approvalSubmitInProgress = false;
  public approvalDatepickerYearRange = `${new Date().getFullYear()}:${new Date().getFullYear() + 10}`;

  public workerWorkflowItem: WorkerCompetencyWorkflowItem;
  public workflowItem: WorkerCompetencyWorkflowItem;
  public today: Date = new Date();

  public rejectionForm: any;
  public showRejectionForm = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private workflowItemService: WorkflowItemService,
    private notificationService: NotificationService,
    private contractorService: ContractorService,
    private workflowAccessService: WorkflowAccessService,
    private globals: Globals,
  ) {
    this.routeRole = this.globals.user.routeRole;
  }

  ngOnInit() {
    const params = this.activatedRoute.snapshot.params as Params;
    const queryParams = this.activatedRoute.snapshot.queryParams as Params;

    this.workflowItemId = params.workflowId;
    this.backLinkParams = readWorkflowBackParams(queryParams);

    this.setupPage();
  }

  private setupPage() {
    // Worker competency approvals are driven from the inbox payload when available.
    // This keeps the click-through flow aligned with the inbox state used to open the dedicated approval page.
    const workflowItemFromCache = this.workflowItemService.getCachedWorkflowItem(this.workflowItemId, 'WORKER_COMPETENCY');

    if (workflowItemFromCache) {
      this.setupPageWithWorkflowItem(workflowItemFromCache);
      // The cache is one-time use so the approval page always consumes the inbox payload once per click-through.
      this.workflowItemService.clearCachedWorkflowItem(this.workflowItemId, 'WORKER_COMPETENCY');
      return;
    }

    setTimeout(() => {
      this.notificationService.notify('warning', 'Cannot open this approval item', 'Please open it from the inbox.');
      this.navigateToInbox();
    });
  }

  private setupPageWithWorkflowItem(workflowItem: WorkflowItem) {
    // Worker competency keeps its own model mapping and save payload.
    this.workerWorkflowItem = workflowItem as WorkerCompetencyWorkflowItem;
    this.workflowItem = workflowItem as WorkerCompetencyWorkflowItem;
    this.canOpenRequestedByDetails = this.workflowAccessService.canOpenWorkerCompetencyManagement();
    this.setupWorkerCompetencyLink();
    this.setupCeritificatePage(this.workerWorkflowItem, this.getBackLinkTabItems());
    this.approvalExpiryDate = this.workerWorkflowItem.expiryDate || null;
  }

  private setupCeritificatePage(workFlowItem: WorkerCompetencyWorkflowItem, tabitems: any[]) {
    if (!workFlowItem) {
      this.notificationService.notify('warning', `Cannot find this approval item`, 'Likely it has already been approved or rejected.');
      this.navigateToInbox();
      return;
    }

    this.isViewOnly = this.isClosedWorkflowItem(workFlowItem.status)
      || this.isOutOfScopeForCurrentRoute(workFlowItem)
      || this.isOwnCompetencyApproval;
    this.title = this.isViewOnly ? 'Worker Certificate Details' : 'Worker Certificate Pending Approval';
    this.tabitems = tabitems;
  }

  private getBackLinkTabItems() {
    const backLinkTabItem: any = { path: '/' + this.routeRole + '/pending-items', title: 'Inbox', class: 'back-link' };

    if (hasWorkflowBackParams(this.backLinkParams)) {
      backLinkTabItem.queryParams = this.backLinkParams;
    }

    return [backLinkTabItem];
  }

  public approve() {
    if (this.isViewOnly) {
      return;
    }

    this.showRejectionForm = false;

    if (!this.showApprovalForm) {
      this.approvalExpiryDateRequired = false;
      this.showApprovalForm = true;
      return;
    }

    this.saveApproval();
  }

  public saveApproval() {
    if (this.isViewOnly) {
      return;
    }

    if (this.approvalSubmitInProgress) {
      return;
    }

    if (!this.approvalExpiryDate) {
      this.approvalExpiryDateRequired = true;
      return;
    }

    if (this.isApprovalDateInPast) {
      this.approvalExpiryDateRequired = false;
      return;
    }

    this.approvalExpiryDateRequired = false;
    this.approvalSubmitInProgress = true;
    this.saveInProgress = true;
    const workerCompetencyId = this.getWorkerCompetencyId();
    const siteOwnerSkylarkUserId = this.getCurrentSiteOwnerId();

    if (!siteOwnerSkylarkUserId) {
      this.approvalSubmitInProgress = false;
      this.saveInProgress = false;
      this.notificationService.notify('danger', 'Missing site owner information', 'Reload the page and try again.');
      return;
    }

    const body = {
      status: 'APPROVED',
      siteOwnerSkylarkUserId,
      expiryDate: DateTimeUtils.localDateToTimelessString(this.approvalExpiryDate),
    };

    this.workflowItemService.approveOrRejectItem(workerCompetencyId, body, 'WORKER_COMPETENCY').subscribe(() => {
      this.saveInProgress = false;
      this.approvalSubmitInProgress = false;
      this.notificationService.notify('success', 'Success', 'Certificate approved');
      this.navigateToInbox();
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', `Couldn't approve certificate`, `Try again later`);
      this.saveInProgress = false;
      this.approvalSubmitInProgress = false;
    });
  }

  public reject() {
    if (this.isViewOnly) {
      return;
    }

    this.showApprovalForm = false;
    this.approvalExpiryDateRequired = false;
    this.showRejectionForm = true;
  }

  public cancelRejection() {
    if (this.isViewOnly) {
      return;
    }

    this.showApprovalForm = false;
    this.showRejectionForm = false;
    this.rejectionForm = '';
    this.saveInProgress = false;
    this.approvalSubmitInProgress = false;
  }

  public cancelApproval() {
    if (this.isViewOnly) {
      return;
    }

    this.showRejectionForm = false;
    this.showApprovalForm = false;
    this.approvalExpiryDateRequired = false;
    this.saveInProgress = false;
    this.approvalSubmitInProgress = false;
  }

  public saveRejection() {
    if (this.isViewOnly) {
      return;
    }

    const trimmedRejectionReason = (this.rejectionForm || '').trim();
    if (!trimmedRejectionReason) {
      return;
    }

    this.saveInProgress = true;
    const workerCompetencyId = this.getWorkerCompetencyId();
    const siteOwnerSkylarkUserId = this.getCurrentSiteOwnerId();

    if (!siteOwnerSkylarkUserId) {
      this.saveInProgress = false;
      this.notificationService.notify('danger', 'Missing site owner information', 'Reload the page and try again.');
      return;
    }

    const body = {
      status: 'DOCUMENT_REJECTED',
      siteOwnerSkylarkUserId,
      expiryDate: this.workerWorkflowItem && this.workerWorkflowItem.expiryDate
        ? DateTimeUtils.localDateToTimelessString(this.workerWorkflowItem.expiryDate)
        : null,
      reason: trimmedRejectionReason,
    };

    this.workflowItemService.approveOrRejectItem(workerCompetencyId, body, 'WORKER_COMPETENCY').subscribe(() => {
      this.saveInProgress = false;
      this.notificationService.notify('success', 'Success', 'Request rejected');
      this.navigateToInbox();
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', `Couldn't save rejection`, `Try again later`);
      this.saveInProgress = false;
    });
  }

  public viewDocument(fileName) {
    this.contractorService.showFile(environment.apiUrl + 'api/ext-file/workerUserCompetency', fileName);
  }

  private navigateToInbox() {
    const navigationExtras: any = {};

    if (hasWorkflowBackParams(this.backLinkParams)) {
      navigationExtras.queryParams = this.backLinkParams;
    }

    this.router.navigate(['/' + this.routeRole + '/pending-items'], navigationExtras);
  }

  private isClosedWorkflowItem(status: string): boolean {
    return ['APPROVED', 'REJECTED', 'DOCUMENT_REJECTED'].indexOf(status) !== -1;
  }

  private getCurrentSiteOwnerId(): number | null {
    if (this.workerWorkflowItem) {
      return this.workerWorkflowItem.siteOwnerSkylarkUserId || null;
    }

    return null;
  }

  public get currentSiteOwnerName(): string {
    const siteOwnerId = this.getCurrentSiteOwnerId();
    const matchingSiteOwner = this.globals.siteOwnerUsers.find((siteOwner) => siteOwner.skylarkUserId === siteOwnerId);
    return matchingSiteOwner ? matchingSiteOwner.name : '';
  }

  public get shouldShowSiteOwnerName(): boolean {
    return this.globals.siteOwnerUsers.length > 1;
  }

  public get isApprovalDateInPast(): boolean {
    if (!this.approvalExpiryDate) {
      return false;
    }

    return DateTimeUtils.isDateExpired(this.approvalExpiryDate);
  }

  private getWorkerCompetencyId(): number {
    return this.workerWorkflowItem ? this.workerWorkflowItem.workerUserCompetencySiteOwnerId : this.workflowItemId;
  }

  private setupWorkerCompetencyLink(): void {
    this.canOpenWorkerCompetencyLink = this.workflowAccessService.canOpenWorkerCompetencyManagement();

    if (!this.canOpenWorkerCompetencyLink) {
      this.workerCompetencyLink = [];
      this.workerCompetencyLinkQueryParams = null;
      return;
    }

    this.workerCompetencyLink = ['/' + this.routeRole, 'workers', this.workerWorkflowItem.workerSkylarkUserId, 'manage-competencies'];
    this.workerCompetencyLinkQueryParams = {
      backLink: '/' + this.routeRole + '/certificate-approval/worker/' + this.workflowItemId,
      siteOwnerId: this.getCurrentSiteOwnerId(),
    };
  }

  public get isOutOfScopeViewOnly(): boolean {
    return !!this.workerWorkflowItem && this.isOutOfScopeForCurrentRoute(this.workerWorkflowItem);
  }

  // The competency API rejects a site delegate approving or rejecting their own competency, so the
  // page opens view-only when a delegate reaches their own item (e.g. by deep link).
  public get isOwnCompetencyApproval(): boolean {
    return !!this.workerWorkflowItem
      && isDelegateManagingSelf(this.globals.user, this.workerWorkflowItem.workerSkylarkUserId);
  }

  private isOutOfScopeForCurrentRoute(workFlowItem: WorkerCompetencyWorkflowItem): boolean {
    if (!hasSseCompetencyRestrictions(this.globals.user, workFlowItem && workFlowItem.siteOwnerSkylarkUserId)) {
      return false;
    }

    return !canManageCompetencyForSseRoute(
      workFlowItem,
      this.globals.user.canEditTurbineCompetencies,
      this.globals.user.canEditNonTurbineCompetencies,
    );
  }
}
